import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { EmployeeRole } from '@/types';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { SalesPortalRestaurantEmployeesDocument } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-restaurant-employees.generated';
import { useCallback, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalUpdateEmployeeRoleMutation } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/blocs/documents/sales-portal-update-employee-role.generated';

interface UseUpdateEmployeeRoleMutatorResult {
  isAdmin: boolean;
  onChange: (isAdminRole: boolean) => Promise<void>;
  pending: boolean;
}

/**
 * Mutator for toggling an employee's admin role.
 *
 * Holds the admin flag locally and optimistically updates it before firing
 * the mutation. On failure it rolls the local state back and toasts a specific
 * error.
 *
 * @param employee - The employee whose role is being updated
 * @returns `{ isAdmin, onChange, pending }`
 *  `isAdmin` reflects the current value;
 *  `onChange(isAdminRole)` fires the mutation;
 *  `pending` is the in-flight flag.
 */
export const useUpdateEmployeeRoleMutator = (
  employee: RestaurantEmployee,
): UseUpdateEmployeeRoleMutatorResult => {
  const { showErrorToast } = useExperienceToasts();
  const [updateEmployeeRole, { loading: pending }] =
    useSalesPortalUpdateEmployeeRoleMutation();
  const [isAdmin, setIsAdmin] = useState(
    employee.restaurant_employee_role === EmployeeRole.Admin,
  );

  const onChange = useCallback(
    async (isAdminRole: boolean) => {
      setIsAdmin(isAdminRole);
      try {
        const { data } = await updateEmployeeRole({
          refetchQueries: [SalesPortalRestaurantEmployeesDocument],
          variables: {
            businessEmployeeUUID: employee.restaurant_employee_id,
            employeeRole: isAdminRole ? EmployeeRole.Admin : EmployeeRole.User,
          },
        });
        if (!data?.update_businesses_employees_by_pk) {
          captureException(
            new Error('update_businesses_employees_by_pk returned null'),
            { extra: { response: data?.update_businesses_employees_by_pk } },
          );
          setIsAdmin(!isAdminRole);
          showErrorToast(
            i18n.t(
              'sales_portal.employee_edit_drawer.access.update_role_error',
            ),
          );
        }
      } catch (error) {
        captureException(error);
        setIsAdmin(!isAdminRole);
        showErrorToast(
          i18n.t('sales_portal.employee_edit_drawer.access.update_role_error'),
        );
      }
    },
    [employee.restaurant_employee_id, showErrorToast, updateEmployeeRole],
  );

  return { isAdmin, onChange, pending };
};
